<template>
  <div id="NfeXmlImportCreation">
    <BreadCrumb :items="breadcrumbItems" />
    <v-card v-if="loading" elevation="2" class="py-5 px-5 mt-1 text-center">
      Aguarde, buscando dados
      <div class="mt20"></div>
      <v-progress-linear indeterminate class="mb-0"></v-progress-linear>
    </v-card>
    <v-card v-else elevation="0" class="pa-0">
      <v-card-title class="header d-flex justify-space-between align-center py-3">
        <h1 class="pa-0">Processo de Importação</h1>
      </v-card-title>
      <v-stepper v-model="step">
        <v-stepper-header style="box-shadow: 1px 4px 3px rgba(23, 23, 23, 0.28); height: 54px">
          <template v-for="(step, index) in steps">
            <v-stepper-step class="py-2 px-6" :step="step.position" :key="`step-${step.position}`">
              {{ step.name }}
            </v-stepper-step>
            <v-divider v-if="index < steps.length - 1" :key="`divider-${index}`"></v-divider>
          </template>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content class="pa-0 pt-2" step="1">
            <ImportXmlNfeStep
              v-if="step === 1"
              :isOpenConfirmation="isOpenConfirmationStep"
              :isBatchDeleteDialogOpen="isBatchDeleteDialogOpen"
              :totalNfes="totalRecords.totalNFes"
              @step1Finished="XmlImportStep"
              @update:isOpenConfirmation="isOpenConfirmationStep = $event"
              @cancelClick="handleCancelClick"
              @confirmClick="nextStep"
              @isBatchDeleteNfes="BatchDeleteNfes"
            />
            <div class="d-flex justify-space-between">
              <v-card-actions class="d-flex pa-2">
                <v-tooltip top :disabled="!isBatchDeleteNfes">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn
                        color="error"
                        large
                        class="flex-grow-1 mr-2"
                        :disabled="isBatchDeleteNfes"
                        :loading="isBatchDeleteNfesLoading"
                        @cancelClick="handleCancelClick"
                        @confirmClick="nextStep"
                        @click="isBatchDeleteDialogOpen = true"
                      >
                        Descartar Nfes
                      </v-btn>
                    </div>
                  </template>
                  <span> {{ BatchDeleteDialogText }} </span>
                </v-tooltip>
              </v-card-actions>
              <v-card-actions class="d-flex v-card-actions pa-2">
                <v-btn color="grey" large class="flex-grow-1 white--text mr-2" to="/imports/manual-boarding-import">
                  Voltar
                </v-btn>
                <v-tooltip top :disabled="!validateStep">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn
                        class="w-100"
                        color="success"
                        large
                        :disabled="validateStep"
                        :loading="isUpdateButtonLoading"
                        @click="openConfirmationStep"
                      >
                        Próximo
                      </v-btn>
                    </div>
                  </template>
                  <span> {{ validationErrorText }} </span>
                </v-tooltip>
              </v-card-actions>
            </div>
          </v-stepper-content>

          <v-stepper-content class="pa-0 pt-2" step="2">
            <DeliveriesStep
              v-if="step === 2"
              ref="DeliveryStep"
              :isOpenConfirmation="isOpenConfirmationStep"
              :isOpenConfirmDelete="isOpenConfirmationMultipleDelete"
              @update:isOpenConfirmation="isOpenConfirmationStep = $event"
              @update:isOpenConfirmDelete="isOpenConfirmationMultipleDelete = $event"
              @step2Finished="XmlImportStep"
              @confirmClick="nextStep"
              @selectedDeliveriesIds="selectedIds = $event"
              @isCreateLoading="isCreateLoading = $event"
              @isAutomaticGroupedLoading="isAutomaticGroupedLoading = $event"
              @isDisabledAutomaticGrouped="DisabledAutomaticGrouped"
            />

            <div class="d-flex justify-space-between">
              <v-card-actions class="d-flex pa-2">
                <v-tooltip top :disabled="!isDisabledAutomaticGrouped">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn
                        color="info"
                        large
                        class="flex-grow-1 mr-2"
                        :disabled="isDisabledAutomaticGrouped"
                        :loading="isAutomaticGroupedLoading"
                        @click="isGroupingDialog(0)"
                      >
                        Agrupamento Automático
                      </v-btn>
                    </div>
                  </template>
                  <span> {{ AutomaticGroupedErrorText }} </span>
                </v-tooltip>
                <v-btn color="info" large class="flex-grow-1 mr-2" :loading="isCreateLoading" @click="createDelivery()">
                  Criar Entrega
                </v-btn>
                <v-tooltip top :disabled="!!selectedIds.length">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn
                        color="error"
                        large
                        class="flex-grow-1 mr-2"
                        @click="isOpenConfirmationMultipleDelete = true"
                        :disabled="selectedIds.length == 0"
                      >
                        Descartar Entregas
                      </v-btn>
                    </div>
                  </template>
                  <span> Nenhuma entrega selecionada </span>
                </v-tooltip>
              </v-card-actions>

              <v-card-actions class="d-flex v-card-actions pa-2">
                <v-btn color="grey" large class="flex-grow-1 mr-2 white--text" @click="prevStep"> Anterior </v-btn>
                <v-tooltip top :disabled="!validateStep">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn class="w-100" color="success" large :disabled="validateStep" @click="openConfirmationStep">
                        Próximo
                      </v-btn>
                    </div>
                  </template>
                  <span> {{ validationErrorText }} </span>
                </v-tooltip>
              </v-card-actions>
            </div>
          </v-stepper-content>
          <v-stepper-content class="pa-0 pt-2" step="3">
            <CtesStep
              v-if="step === 3"
              ref="CtesStep"
              :isOpenConfirmation="isOpenConfirmationStep"
              @update:isOpenConfirmation="isOpenConfirmationStep = $event"
              @stepCtesFinished="XmlImportStep"
              @confirmClick="nextStep"
            />

            <div class="d-flex justify-end">
              <v-card-actions class="d-flex v-card-actions justify-end pa-2">
                <v-btn color="grey" large class="flex-grow-1 mr-2 white--text" @click="prevStep"> Anterior </v-btn>
                <v-tooltip top :disabled="!validateStep">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn class="w-100" color="success" large :disabled="validateStep" @click="openConfirmationStep">
                        Próximo
                      </v-btn>
                    </div>
                  </template>
                  <span> {{ validationErrorText }} </span>
                </v-tooltip>
              </v-card-actions>
            </div>
          </v-stepper-content>
          <v-stepper-content class="pa-0 pt-2" step="4">
            <stops-step
              v-if="step === 4"
              ref="StopsStep"
              :isOpenConfirmation="isOpenConfirmationStep"
              :isOpenConfirmDelete="isOpenConfirmationMultipleDelete"
              @step3Finished="XmlImportStep"
              @isCreateLoading="isCreateLoading = $event"
              @isAutomaticGroupedLoading="isAutomaticGroupedLoading = $event"
              @confirmClick="nextStep"
              @isDisabledAutomaticGrouped="DisabledAutomaticGrouped"
              @update:isOpenConfirmation="isOpenConfirmationStep = $event"
              @update:isOpenConfirmDelete="isOpenConfirmationMultipleDelete = $event"
              @selectedStopsIds="selectedIds = $event"
            />
            <div class="d-flex justify-space-between">
              <v-card-actions class="d-flex pa-2">
                <v-tooltip top :disabled="!isDisabledAutomaticGrouped">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn
                        color="info"
                        large
                        class="flex-grow-1 mr-2"
                        :disabled="isDisabledAutomaticGrouped"
                        :loading="isAutomaticGroupedLoading"
                        @click="isGroupingDialog(1)"
                      >
                        Agrupamento Automático
                      </v-btn>
                    </div>
                  </template>
                  <span> {{ AutomaticGroupedErrorText }} </span>
                </v-tooltip>
                <v-btn color="info" large class="flex-grow-1 mr-2" :loading="isCreateLoading" @click="createStop()">
                  Criar Parada
                </v-btn>
                <v-tooltip top :disabled="!!selectedIds.length">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn
                        color="error"
                        large
                        class="flex-grow-1 mr-2"
                        @click="isOpenConfirmationMultipleDelete = true"
                        :disabled="selectedIds.length == 0"
                      >
                        Descartar Paradas
                      </v-btn>
                    </div>
                  </template>
                  <span> Nenhuma parada selecionada </span>
                </v-tooltip>
              </v-card-actions>
              <v-card-actions class="d-flex v-card-actions pa-2">
                <v-btn color="grey" large class="flex-grow-1 mr-2 white--text" @click="prevStep"> Anterior </v-btn>
                <v-tooltip top :disabled="!validateStep">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn class="w-100" color="success" large :disabled="validateStep" @click="openConfirmationStep">
                        Próximo
                      </v-btn>
                    </div>
                  </template>
                  <span> {{ validationErrorText }} </span>
                </v-tooltip>
              </v-card-actions>
            </div>
          </v-stepper-content>
          <v-stepper-content class="pa-0 pt-2" step="5">
            <CargosStep
              v-if="step === 5"
              ref="CargosStep"
              :isOpenConfirmation="isOpenConfirmationStep"
              :isOpenConfirmDelete="isOpenConfirmationMultipleDelete"
              @step4Finished="XmlImportStep"
              @isDialogFormOpen="isDialogFormOpen = $event"
              @isCreateLoading="isCreateLoading = $event"
              @isAutomaticGroupedLoading="isAutomaticGroupedLoading = $event"
              @confirmClick="nextStep"
              @isDisabledAutomaticGrouped="DisabledAutomaticGrouped"
              @update:isOpenConfirmation="isOpenConfirmationStep = $event"
              @update:isOpenConfirmDelete="isOpenConfirmationMultipleDelete = $event"
              @selectedCargosIds="selectedIds = $event"
            />
            <div class="d-flex justify-space-between">
              <v-card-actions class="d-flex pa-2">
                <v-tooltip top :disabled="!isDisabledAutomaticGrouped">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn
                        color="info"
                        large
                        class="flex-grow-1 mr-2"
                        :disabled="isDisabledAutomaticGrouped"
                        :loading="isAutomaticGroupedLoading"
                        @click="isGroupingDialog(2)"
                      >
                        Agrupamento Automático
                      </v-btn>
                    </div>
                  </template>
                  <span> {{ AutomaticGroupedErrorText }} </span>
                </v-tooltip>
                <v-btn
                  color="info"
                  large
                  class="flex-grow-1 mr-2"
                  :loading="isCreateLoading"
                  @click="openCreateDialog()"
                >
                  Criar Carga
                </v-btn>
                <v-tooltip top :disabled="!!selectedIds.length">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn
                        color="error"
                        large
                        class="flex-grow-1 mr-2"
                        @click="isOpenConfirmationMultipleDelete = true"
                        :disabled="selectedIds.length == 0"
                      >
                        Descartar Cargas
                      </v-btn>
                    </div>
                  </template>
                  <span> Nenhuma carga selecionada </span>
                </v-tooltip>
              </v-card-actions>

              <v-card-actions class="d-flex v-card-actions pa-2">
                <v-btn color="grey" large class="flex-grow-1 mr-2 white--text" @click="prevStep"> Anterior </v-btn>
                <v-tooltip top :disabled="!validateStep">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn class="w-100" color="success" large :disabled="validateStep" @click="openConfirmationStep">
                        Próximo
                      </v-btn>
                    </div>
                  </template>
                  <span> {{ validationErrorText }} </span>
                </v-tooltip>
              </v-card-actions>
            </div>
          </v-stepper-content>
          <v-stepper-content class="pa-0 pt-2" step="6">
            <BoardingsStep
              v-if="step === 6"
              ref="BoardingsStep"
              :isOpenConfirmation="isOpenConfirmationStep"
              :isOpenConfirmDelete="isOpenConfirmationMultipleDelete"
              @step5Finished="XmlImportStep"
              @isDialogFormOpen="isDialogFormOpen = $event"
              @isCreateLoading="isCreateLoading = $event"
              @isAutomaticGroupedLoading="isAutomaticGroupedLoading = $event"
              @confirmClick="nextStep"
              @update:isOpenConfirmation="isOpenConfirmationStep = $event"
              @update:isOpenConfirmDelete="isOpenConfirmationMultipleDelete = $event"
              @selectedBoardingsIds="selectedIds = $event"
            />
            <div class="d-flex justify-space-between">
              <v-card-actions class="d-flex pa-2">
                <v-btn
                  color="info"
                  large
                  class="flex-grow-1 mr-2"
                  :loading="isCreateLoading"
                  @click="openCreateDialog()"
                >
                  Criar Embarque
                </v-btn>
                <v-tooltip top :disabled="!!selectedIds.length">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn
                        color="error"
                        large
                        class="flex-grow-1 mr-2"
                        @click="isOpenConfirmationMultipleDelete = true"
                        :disabled="selectedIds.length == 0"
                      >
                        Descartar Embarques
                      </v-btn>
                    </div>
                  </template>
                  <span> Nenhum embarque selecionado </span>
                </v-tooltip>
              </v-card-actions>

              <v-card-actions class="d-flex v-card-actions pa-2">
                <v-btn color="grey" large class="flex-grow-1 mr-2 white--text" @click="prevStep"> Anterior </v-btn>
                <v-tooltip top :disabled="!validateStep">
                  <template #activator="{ on, attrs }">
                    <div class="flex-grow-1" v-bind="attrs" v-on="on">
                      <v-btn class="w-100" color="success" large :disabled="validateStep" @click="openConfirmationStep">
                        Próximo
                      </v-btn>
                    </div>
                  </template>
                  <span> {{ validationErrorText }} </span>
                </v-tooltip>
              </v-card-actions>
            </div>
          </v-stepper-content>
          <v-stepper-content class="pa-0 pt-2" step="7">
            <ConfirmationStepProcess
              v-if="step === 7"
              ref="BoardingsStep"
              :isOpenConfirmation="isOpenConfirmationStep"
              :process="process"
              @update:isOpenConfirmation="isOpenConfirmationStep = $event"
              @confirmClick="nextStep"
            />
            <div class="d-flex justify-end">
              <v-card-actions class="d-flex v-card-actions pa-2">
                <v-btn
                  v-if="process?.stepName == 'Concluído'"
                  color="grey"
                  large
                  class="w-100 flex-grow-1 white--text mr-2"
                  to="/imports/manual-boarding-import"
                >
                  Voltar
                </v-btn>
                <v-btn v-else color="grey" large class="flex-grow-1 mr-2 white--text" @click="prevStep">
                  Anterior
                </v-btn>
                <div class="flex-grow-1">
                  <v-btn
                    v-if="process?.stepName != 'Concluído'"
                    class="w-100"
                    color="success"
                    large
                    @click="openConfirmationStep"
                  >
                    Confirmar
                  </v-btn>
                </div>
              </v-card-actions>
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card>
    <confirm-grouping
      :isOpen.sync="isGroupingDialogOpen"
      :bodyText="bodyText"
      @cancelClick="isGroupingDialogOpen = false"
      @confirmClick="createHierarchy"
    />
    <confirm-dialog-form
      v-if="isBoarding != null"
      :isOpen.sync="isDialogFormOpen"
      :isBoarding="isBoarding"
      :isGrouping="isGrouped"
      @cancelClick="
        isDialogFormOpen = false;
        isGrouped = false;
      "
      @confirmCargoClick="createCargo"
      @confirmBoardingClick="createBoarding"
      @validateTransfer="validateTransfer"
    />
    <MessagesError :errors="errors" :type="errorsType" :error500="error500" @closeMessages="errors = []" />
  </div>
</template>

<script>
import BreadCrumb from '@/components/BreadCrumb.vue';
import MessagesError from '@/components/MessagesError.vue';
import ImportXmlNfeStep from '@/components/ManualBoardingImport/Stepper/Step1/ImportXmlNfeStep.vue';
import DeliveriesStep from '@/components/ManualBoardingImport/Stepper/Step2/DeliveriesStep.vue';
import CtesStep from '@/components/ManualBoardingImport/Stepper/Step3/CtesStep.vue';
import StopsStep from '@/components/ManualBoardingImport/Stepper/Step4/StopsStep.vue';
import CargosStep from '@/components/ManualBoardingImport/Stepper/Step5/CargosStep.vue';
import BoardingsStep from '@/components/ManualBoardingImport/Stepper/Step6/BoardingsStep.vue';
import ConfirmationStepProcess from '@/components/ManualBoardingImport/Stepper/Step7/ConfirmationStep.vue';
import ConfirmGrouping from '@/components/ManualBoardingImport/Stepper/ConfirmGrouping.vue';
import ConfirmDialogForm from '@/components/ManualBoardingImport/Stepper/ConfirmDialogForm.vue';
import { FeedbackMessageHandler } from '@/store/modules/feedbackMessageHandler';
import { mapActions } from 'vuex';

export default {
  components: {
    BreadCrumb,
    MessagesError,
    ImportXmlNfeStep,
    DeliveriesStep,
    CtesStep,
    StopsStep,
    CargosStep,
    BoardingsStep,
    ConfirmationStepProcess,
    ConfirmGrouping,
    ConfirmDialogForm
  },
  data: () => ({
    step: null,
    validateStep: true,
    validationErrorText: '',
    bodyText: '',
    AutomaticGroupedErrorText: '',
    dataTableValidation: false,
    isOpenConfirmationStep: false,
    isOpenConfirmationMultipleDelete: false,
    loading: true,
    isCreateLoading: false,
    isUpdateButtonLoading: false,
    isAutomaticGroupedLoading: false,
    isDisabledAutomaticGrouped: true,
    isBatchDeleteNfesLoading: false,
    isBatchDeleteDialogOpen: false,
    isDialogFormOpen: false,
    isBatchDeleteNfes: true,
    BatchDeleteDialogText: '',
    selectedIds: [],
    process: {},
    isGrouped: false,
    errors: [],
    errorsType: 'error',
    error500: null,
    hierarchyRecords: [],
    totalRecords: [],
    breadcrumbItems: [
      {
        text: 'Início',
        disabled: false,
        to: '/start'
      },
      {
        text: 'Processos de Importação',
        exact: true,
        to: '/imports/manual-boarding-import',
        disabled: false
      },
      {
        text: 'Visualizar Processo',
        disabled: true
      }
    ],
    steps: [
      { position: 1, name: 'Importar NFes', id: '6ef28252-ef1c-4a2a-a941-ea8c1cc2ca03' },
      { position: 2, name: 'Entregas', id: 'bbc452e5-d7b0-4745-bced-a7c652ed9c0d' },
      { position: 3, name: 'Detalhes das entregas', id: '655ac746-cb01-4461-9e8d-710298e23c23' },
      { position: 4, name: 'Paradas', id: 'a37d23f4-b680-420b-8273-1e6a1921b81e' },
      { position: 5, name: 'Cargas', id: 'c292c4b9-dcb4-487b-af53-aa8087fdb4aa' },
      { position: 6, name: 'Embarques', id: '0c428bf3-d0bb-48b2-9d03-d04b0ab8bfc4' },
      {
        position: 7,
        name: 'Confirmação',
        ids: ['f658765e-699c-498b-840a-5455a7d3d2ca', '54a9ebe0-1610-456a-97f0-cc90be72b036']
      }
    ],
    isGroupingDialogOpen: false
  }),
  mounted() {
    this.fetchProcessByID();
    this.fetchQuantitiesHierarchy();
  },
  computed: {
    isBoarding() {
      if (this.step === 6) {
        return true;
      } else if (this.step === 5) {
        return false;
      }
      return null;
    },
    parameterID() {
      return this.$route.params.id;
    }
  },
  methods: {
    ...mapActions('ImportManualDashboardStore', ['fetchQuantitiesHierarchyRecords']),
    ...mapActions('ManualBoardingStore', [
      'fetchSummarySteps',
      'fetchManualBoardingByID',
      'confirmImportsNfes',
      'updateManualBoardingSteps',
      'createManualBoardingImport',
      'startManualBoardingImport'
    ]),

    async nextStep(selectedNfeIds) {
      this.isOpenConfirmationStep = false;

      if (this.step === 7) {
        const response = await this.startManualBoardingImport(this.process.id);
        if (response.success) {
          FeedbackMessageHandler.setFeedbackMessage(
            this,
            `Importando os embarques do processo ${this.process.manualBoardingImportNumber}`
          );
          this.$router.push('/imports/manual-boarding-import');
        } else {
          this.errors = response.errors || ['Falha ao Processar Embarques manuais!'];
          this.errorsType = 'error';
        }
      }
      if (this.step === 1 && !this.validateStep) {
        await this.confirmImportsStep1(selectedNfeIds);
        await this.updateStep(true);
      } else if (!this.validateStep) {
        await this.updateStep(true);
      }
      if (this.step === 7) {
        this.fetchProcessByID();
      }
      this.validateStep = true;
      this.validationErrorText = '';
    },

    async prevStep() {
      if (this.step > 1) {
        await this.updateStep();
      }
      this.selectedIds = [];
      this.validateStep = false;
      this.validationErrorText = '';
    },

    async fetchProcessByID() {
      this.loading = true;
      try {
        this.process = await this.fetchManualBoardingByID(this.parameterID);
        if (this.process?.stepName == 'Concluído') {
          this.step = 7;
        } else if (this.process?.stepsId) {
          const current = this.steps.find((step) =>
            Array.isArray(step.ids) ? step.ids.includes(this.process.stepsId) : step.id === this.process.stepsId
          );

          if (current) {
            this.step = current.position;
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    async fetchQuantitiesHierarchy() {
      try {
        const data = await this.fetchQuantitiesHierarchyRecords(this.parameterID);
        this.hierarchyRecords = [
          { title: 'Total de NFes', count: data.nfesQuantity },
          { title: 'Entregas', count: data.deliveriesQuantity },
          { title: 'Paradas', count: data.stopsQuantity },
          { title: 'Cargas - entrega direta', count: data.directDeliveryCargosQuantity },
          { title: 'Cargas - transferências', count: data.transhipmentCargosQuantity },
          { title: 'Embarques', count: data.boardingsQuantity }
        ];
        this.totalRecords = {
          totalNFes: data.nfesQuantity,
          totalDeliveries: data.deliveriesQuantity,
          totalStops: data.stopsQuantity,
          totalCargos: data.directDeliveryCargosQuantity + data.transhipmentCargosQuantity,
          totalBoardings: data.boardingsQuantity
        };
      } catch (err) {
        console.error(err);
      }
    },
    validateTransfer(errors, errorsType) {
      this.errors = errors;
      this.errorsType = errorsType;
    },
    createHierarchy() {
      if (this.bodyText === 'Cargas') {
        this.createCargo(true);
      } else if (this.bodyText === 'Paradas') {
        this.createStop(true);
      } else if (this.bodyText === 'Entregas') {
        this.createDelivery(true);
      }
    },
    isGroupingDialog(step) {
      if (step === 0) {
        this.bodyText = 'Entregas';
        this.isGroupingDialogOpen = true;
      } else if (step === 1) {
        this.bodyText = 'Paradas';
        this.isGroupingDialogOpen = true;
      } else if (step === 2) {
        this.bodyText = 'Cargas';
        this.isGroupingDialogOpen = true;
      } else if (step === 3) {
        this.isGrouped = true;
        this.isDialogFormOpen = true;
      }
    },
    openCreateDialog() {
      this.isGrouped = false;
      this.isDialogFormOpen = true;
    },
    createDelivery(value) {
      this.$refs.DeliveryStep.addDelivery(value);
      this.isGroupingDialogOpen = false;
    },
    createStop(value) {
      this.$refs.StopsStep.addStop(value);
      this.isGroupingDialogOpen = false;
    },
    createCargo(isGrouped, value) {
      this.$refs.CargosStep.addCargo(isGrouped, value);
      this.isGroupingDialogOpen = false;
    },
    createBoarding(value) {
      this.$refs.BoardingsStep.addBoarding(value);
      this.isDialogFormOpen = false;
      this.isGrouped = false;
    },
    async updateStep(next = false) {
      next ? this.step++ : this.step--;
      const currentStep = this.steps.find((x) => x.position === this.step);
      if (currentStep.position === 7) {
        this.loading = true;
      }
      const model = {
        id: this.parameterID,
        step: currentStep?.id || (Array.isArray(currentStep?.ids) ? currentStep.ids[0] : undefined)
      };

      try {
        this.isUpdateButtonLoading = true;
        await this.updateManualBoardingSteps(model);
        await this.updateUrl();
      } catch (error) {
        this.errors = error.errors;
        this.errorsType = 'error';
        console.error(error);
      } finally {
        this.isUpdateButtonLoading = false;
      }
    },
    async confirmImportsStep1(selectedNfeIds) {
      try {
        const model = {
          processId: this.parameterID,
          unusedNfes: selectedNfeIds
        };
        await this.confirmImportsNfes(model);
      } catch (error) {
        this.errors = error.errors;
        this.errorsType = 'error';
        console.error(error);
      }
    },
    updateUrl() {
      const currentUrl = window.location.href;
      const parts = currentUrl.split('/');
      parts[parts.length - 1] = `step${this.step}`;
      const newUrl = parts.join('/');
      history.replaceState(null, '', newUrl);
    },
    XmlImportStep(errorValidate, message) {
      this.validateStep = errorValidate;
      this.validationErrorText = message;
    },
    DisabledAutomaticGrouped(message, disabled) {
      this.isDisabledAutomaticGrouped = disabled;
      this.AutomaticGroupedErrorText = message;
    },
    BatchDeleteNfes(disabled, message) {
      this.isBatchDeleteNfes = disabled;
      this.BatchDeleteDialogText = message;
    },
    handleCancelClick() {
      this.isDialogFormOpen = false;
      this.isOpenConfirmationStep = false;
      this.isBatchDeleteDialogOpen = false;
    },
    openConfirmationStep() {
      this.isOpenConfirmationStep = true;
    }
  }
};
</script>
<style scoped>
.v-card-actions {
  width: 300px;
}
</style>
